import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import Wizard from "components/wizard";
import Layout from "layout";
import Loader from "components/loader";
import { t } from "intl/index";
import ThankYouForAnswering from "components/thankYouForAnswering";
import useAppStore from "stores/appStore";
import { MULTICHOICE_OTHER_KEY } from "components/multichoiceQuestion";

// Stable reference so the wizard's restore effect doesn't reset in-progress input on re-render.
const EMPTY_ANSWERS = [];

const generateRunId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isConfiguredMultichoice = (question) => {
  const options = question?.multichoice?.options;
  return question?.response_mode === "multichoice" && Array.isArray(options) && options.length >= 2;
};

const WizardContainer = (props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [options] = useState(props.formData.options ?? []);
  const [showThankYou, setShowThankYou] = useState(false);
  const [apiError, setApiError] = useState("");
  const [version, setVersion] = useState(null);

  const { tLoading, lang, questions, testMode } = useAppStore();

  const runId = useMemo(() => (testMode ? generateRunId() : null), [testMode]);

  const url = new URL(window.location);
  const hash = url.searchParams.get("hash");
  const surveyId = url.searchParams.get("surveyId");

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/v1/version`
        );
        setVersion(data.version);
      } catch (error) {
        console.error(`Error fetching version: ${error.message}`);
      }
    };

    fetchVersion();
  }, []);

  if (tLoading) {
    return <Loader title={t(lang).LOADING || "Loading..."} />;
  }

  const handleNext = (values) => {
    const nextStep = activeStep + 1;
    const questionKey = String(questions[activeStep].id);

    // Drop the stored answer when a step is left empty so a cleared answer is not resubmitted.
    const nextAnswers = { ...answers };
    if (Array.isArray(values) && values.length > 0) {
      nextAnswers[questionKey] = values;
    } else {
      delete nextAnswers[questionKey];
    }
    setAnswers(nextAnswers);
    setApiError("");
    setActiveStep(nextStep);

    if (activeStep === questions.length - 1) {
      submit(nextAnswers);
    }
  };

  const returnToLastQuestion = (message) => {
    setApiError(message);
    setShowThankYou(false);
    setActiveStep(Math.max(questions.length - 1, 0));
  };

  const submit = async (finalAnswers) => {
    if (!version) {
      console.error("Version is not available, cannot submit data.");
      returnToLastQuestion(t(lang).SUBMIT_ERROR || "Failed to submit answers.");
      return;
    }

    setIsSubmitting(true);
    const { participantAccessToken } = props;

    if (!surveyId) {
      setIsSubmitting(false);
      return;
    }

    const questionById = new Map(questions.map((question) => [String(question.id), question]));
    const configuredMultichoiceIds = new Set(
      questions
        .filter((question) => isConfiguredMultichoice(question))
        .map((question) => String(question.id))
    );

    const edges = Object.entries(finalAnswers)
      .filter(([questionId]) => !configuredMultichoiceIds.has(String(questionId)))
      .flatMap(([questionId, values]) =>
        values.map((value) => {
          const question = questionById.get(String(questionId));
          const edge = {
            survey_id: surveyId,
            from_id: hash,
            to_id: value.value,
            question_id: String(questionId),
            weight: typeof value.weight === "number" ? value.weight : 1,
          };

          if (question?.response_mode === "nomination" || !question?.response_mode) {
            edge.weight = 1;
          }

          if (testMode && runId) {
            edge.run_id = runId;
          }
          return edge;
        })
      );

    const multichoiceAnswers = Object.entries(finalAnswers)
      .filter(([questionId]) => configuredMultichoiceIds.has(String(questionId)))
      .map(([questionId, values]) => {
        const answer = {
          question_id: String(questionId),
          option_key: String(values?.[0]?.option_key || "").trim(),
        };
        if (answer.option_key === MULTICHOICE_OTHER_KEY) {
          answer.other_text = String(values?.[0]?.other_text || "").trim();
        }
        return answer;
      })
      .filter((answer) => answer.option_key);

    const hasConfiguredMultichoiceQuestion = configuredMultichoiceIds.size > 0;
    const shouldUseWrappedPayload = hasConfiguredMultichoiceQuestion || edges.length === 0;

    const wrappedPayload = {
      edges,
      participant_id: hash,
      survey_id: Number(surveyId),
      run_id: testMode && runId ? runId : "",
    };

    if (hasConfiguredMultichoiceQuestion || multichoiceAnswers.length > 0) {
      wrappedPayload.multichoice_answers = multichoiceAnswers;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_FASTIFY_API_URL}/${version}/aona-edges`,
        shouldUseWrappedPayload ? wrappedPayload : edges,
        {
          headers: { Authorization: `Bearer ${participantAccessToken}` },
        }
      );

      setTimeout(() => {
        setShowThankYou(true);
      }, 500);
      setApiError("");
    } catch (err) {
      console.error("Error submitting survey:", err);
      returnToLastQuestion(
        err?.response?.data?.message || t(lang).SUBMIT_ERROR || "Failed to submit answers."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  if (showThankYou) {
    return <ThankYouForAnswering thankYouPageString={props.thankYouPageString} />;
  }

  if (isSubmitting) {
    return <Loader title={t(lang).SENDING_DATA || "Sending answer..."} />;
  }

  if (activeStep === questions.length) {
    return null;
  }

  return (
    <Layout settings={props.settings}>
      <Wizard
        onNextClick={handleNext}
        activeStep={activeStep}
        questions={questions}
        options={options}
        onBackClick={handleBack}
        settings={props.settings}
        prevAnswers={answers[String(questions[activeStep].id)] || EMPTY_ANSWERS}
        apiError={apiError}
      />
    </Layout>
  );
};

export default WizardContainer;
